/**
 * Re-read every surface painting an address, after something about it changed.
 *
 * An address is drawn from half a dozen caches at once — the batch a graph
 * resolves its nodes through, the single-address read a detail panel makes, the
 * stored row a header reads, the cross-chain read a detail page opens on, the
 * list a table pages — and a write touches all of them at the same moment.
 * Before this, each write invalidated whichever ones its own caller happened to
 * know about: setting a risk band from the case inspector refetched the panel's
 * row, so the badge beside the control updated and the graph node behind it kept
 * the colour it had until a reload; setting the SAME band from the detail page's
 * edit dialog refetched the four address reads and left every table, graph and
 * counterparty column on the old band.
 *
 * So there is one list and one hook, and a writer of an address row calls it
 * rather than assembling its own. A write path adding its own invalidation is
 * exactly how these drift apart again.
 *
 * Keyed by PREFIX, deliberately. A batch's key carries the whole ref set it was
 * fetched for, so an address appears in as many keys as there are graphs on
 * screen; matching the prefix catches all of them without the caller having to
 * know which. Over-invalidating a few address queries costs a refetch, and the
 * failure it replaces is a graph that quietly disagrees with the panel on top of
 * it.
 */
import * as React from "react";
import { useQueryClient } from "@tanstack/react-query";

import { ADDRESS_QUERY_PREFIXES } from "@/api/queryResourcePolicies";
export { ADDRESS_QUERY_PREFIXES } from "@/api/queryResourcePolicies";

export function useInvalidateAddressOverlays(): () => void {
  const queryClient = useQueryClient();
  return React.useCallback(() => {
    for (const queryKey of ADDRESS_QUERY_PREFIXES) {
      void queryClient.invalidateQueries({ queryKey });
    }
  }, [queryClient]);
}
